import React from 'react';
import Icon from 'components/AppIcon';

const MarketSettingsForm = ({ marketData, updateMarketData, validationErrors }) => {
  const feeStructures = [
    { value: 0, label: '0% - No fee', description: 'Market creator receives no fees' },
    { value: 1, label: '1% - Low fee', description: 'Recommended for high-volume markets' },
    { value: 2, label: '2% - Standard fee', description: 'Most popular option' },
    { value: 3, label: '3% - Medium fee', description: 'Good for specialized markets' },
    { value: 5, label: '5% - High fee', description: 'For premium or niche markets' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading-semibold text-text-primary mb-6">Market Settings</h2>
        <p className="text-text-secondary mb-8">
          Configure trading parameters and resolution criteria for your market.
        </p>
      </div>

      {/* Betting Limits */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-body-semibold text-text-primary mb-2">
            Minimum Bet Amount ($)
          </label>
          <input
            type="number"
            value={marketData?.minBetAmount}
            onChange={(e) => updateMarketData('minBetAmount', parseFloat(e?.target?.value) || 0)}
            min="0.01"
            step="0.01"
            className={`w-full px-4 py-3 border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary ${
              validationErrors?.minBetAmount ? 'border-error' : 'border-border'
            }`}
          />
          {validationErrors?.minBetAmount && (
            <p className="mt-2 text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {validationErrors?.minBetAmount}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-body-semibold text-text-primary mb-2">
            Maximum Bet Amount ($)
          </label>
          <input
            type="number"
            value={marketData?.maxBetAmount}
            onChange={(e) => updateMarketData('maxBetAmount', parseFloat(e?.target?.value) || 0)}
            min={marketData?.minBetAmount}
            step="1"
            className={`w-full px-4 py-3 border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary ${
              validationErrors?.maxBetAmount ? 'border-error' : 'border-border'
            }`}
          />
          {validationErrors?.maxBetAmount && (
            <p className="mt-2 text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {validationErrors?.maxBetAmount}
            </p>
          )}
        </div>
      </div>

      {/* Creator Fee Structure */}
      <div>
        <label className="block text-sm font-body-semibold text-text-primary mb-2">
          Creator Fee Structure
        </label>
        <div className="space-y-3">
          {feeStructures?.map((fee) => (
            <label
              key={fee?.value}
              className={`flex items-center space-x-3 p-4 border rounded-trading cursor-pointer transition-all duration-trading-fast ${
                marketData?.creatorFee === fee?.value
                  ? 'bg-primary-900 border-primary-800 text-primary-300' :'bg-surface-hover border-border text-text-secondary hover:text-text-primary hover:border-border-hover'
              }`}
            >
              <input
                type="radio"
                name="creatorFee"
                value={fee?.value}
                checked={marketData?.creatorFee === fee?.value}
                onChange={() => updateMarketData('creatorFee', fee?.value)}
                className="sr-only"
              />
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                marketData?.creatorFee === fee?.value
                  ? 'border-primary bg-primary' :'border-border'
              }`}>
                {marketData?.creatorFee === fee?.value && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
              <div className="flex-1">
                <p className="font-body-medium text-sm">{fee?.label}</p>
                <p className="text-xs opacity-70">{fee?.description}</p>
              </div>
            </label>
          ))}
        </div>
        {validationErrors?.creatorFee && (
          <p className="mt-2 text-sm text-error flex items-center">
            <Icon name="AlertCircle" size={16} className="mr-1" />
            {validationErrors?.creatorFee}
          </p>
        )}
      </div>

      {/* Resolution Source */}
      <div>
        <label className="block text-sm font-body-semibold text-text-primary mb-2">
          Resolution Source (Optional)
        </label>
        <input
          type="text"
          value={marketData?.resolutionSource}
          onChange={(e) => updateMarketData('resolutionSource', e?.target?.value)}
          placeholder="e.g., CoinMarketCap, Reuters, Official Government Website..."
          className="w-full px-4 py-3 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary"
        />
        <p className="mt-2 text-xs text-text-secondary">
          Specify the authoritative source that will be used to resolve this market.
        </p>
      </div>

      {/* Resolution Criteria */}
      <div>
        <label className="block text-sm font-body-semibold text-text-primary mb-2">
          Resolution Criteria *
        </label>
        <textarea
          value={marketData?.resolutionCriteria}
          onChange={(e) => updateMarketData('resolutionCriteria', e?.target?.value)}
          placeholder="Clearly define how this market will be resolved. Include specific conditions, data sources, and edge cases..."
          rows={4}
          maxLength={1000}
          className={`w-full px-4 py-3 border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary resize-none ${
            validationErrors?.resolutionCriteria ? 'border-error' : 'border-border'
          }`}
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-text-secondary">
            Clear resolution criteria prevent disputes and build trader confidence.
          </p>
          <span className="text-xs text-text-secondary">
            {marketData?.resolutionCriteria?.length || 0}/1000
          </span>
        </div>
        {validationErrors?.resolutionCriteria && (
          <p className="mt-2 text-sm text-error flex items-center">
            <Icon name="AlertCircle" size={16} className="mr-1" />
            {validationErrors?.resolutionCriteria}
          </p>
        )}
      </div>

      {/* Advanced Options */}
      <div className="space-y-4">
        <h3 className="text-lg font-body-semibold text-text-primary">Advanced Options</h3>
        
        {/* Public/Private Market */}
        <div className="flex items-center justify-between p-4 bg-surface-hover rounded-trading border border-border">
          <div>
            <p className="font-body-medium text-sm text-text-primary">Public Market</p>
            <p className="text-xs text-text-secondary">Allow anyone to discover and trade on this market</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={marketData?.isPublic}
              onChange={(e) => updateMarketData('isPublic', e?.target?.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-hover peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>

        {/* Early Resolution */}
        <div className="flex items-center justify-between p-4 bg-surface-hover rounded-trading border border-border">
          <div>
            <p className="font-body-medium text-sm text-text-primary">Allow Early Resolution</p>
            <p className="text-xs text-text-secondary">Market can be resolved before the end date if outcome becomes certain</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={marketData?.allowEarlyResolution}
              onChange={(e) => updateMarketData('allowEarlyResolution', e?.target?.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-hover peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>
      </div>

      {/* Fee Explanation */}
      <div className="bg-primary-900 bg-opacity-20 border border-primary-800 rounded-trading p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div>
            <p className="font-body-medium text-sm text-text-primary mb-1">How Creator Fees Work</p>
            <p className="text-xs text-text-secondary leading-relaxed">
              Creator fees are deducted from winning payouts and paid to the market creator. 
              A {marketData?.creatorFee}% fee means you'll earn ${marketData?.creatorFee} for every $100 in winning trades. 
              Lower fees typically attract more traders, while higher fees provide more revenue per trade.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketSettingsForm;